import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ChevronDownIcon,
  BellIcon,
  CheckIcon,
  EyeIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftIcon,
  InformationCircleIcon,
  UserIcon,
  ArrowPathIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NotificationHistory = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // list or timeline

  useEffect(() => {
    // Simulate fetching notification history
    const fetchNotificationHistory = () => {
      const mockHistory = [
        {
          id: 'HIST001',
          title: 'Quarterly Performance Review Completed',
          message: 'Your quarterly performance review has been completed and is available for download.',
          category: 'forms',
          type: 'completion',
          priority: 'medium',
          timestamp: '2025-01-22T16:30:00Z',
          sender: {
            name: 'HR Department',
            avatar: 'https://ui-avatars.com/api/?name=HR+Department&background=3b82f6&color=fff',
            role: 'Human Resources'
          },
          actionTaken: 'viewed',
          readAt: '2025-01-22T17:00:00Z',
          relatedModule: 'forms'
        },
        {
          id: 'HIST002',
          title: 'Medical Equipment Maintenance Scheduled',
          message: 'Routine maintenance for MRI Machine #3 has been scheduled for January 25th at 8:00 AM.',
          category: 'tasks',
          type: 'scheduling',
          priority: 'high',
          timestamp: '2025-01-21T14:15:00Z',
          sender: {
            name: 'Maintenance Team',
            avatar: 'https://ui-avatars.com/api/?name=Maintenance+Team&background=10b981&color=fff',
            role: 'Maintenance'
          },
          actionTaken: 'acknowledged',
          readAt: '2025-01-21T14:30:00Z',
          relatedModule: 'tasks'
        },
        {
          id: 'HIST003',
          title: 'Training Module: Patient Safety Protocols - Completed',
          message: 'Congratulations! You have successfully completed the Patient Safety Protocols training module.',
          category: 'training',
          type: 'completion',
          priority: 'medium',
          timestamp: '2025-01-20T11:45:00Z',
          sender: {
            name: 'Training System',
            avatar: 'https://ui-avatars.com/api/?name=Training+System&background=8b5cf6&color=fff',
            role: 'System'
          },
          actionTaken: 'viewed',
          readAt: '2025-01-20T12:00:00Z',
          relatedModule: 'training'
        },
        {
          id: 'HIST004',
          title: 'Document Access Granted: Research Protocol V2.1',
          message: 'You have been granted access to the updated research protocol document. Please review the changes highlighted in yellow.',
          category: 'documents',
          type: 'access_granted',
          priority: 'medium',
          timestamp: '2025-01-19T09:30:00Z',
          sender: {
            name: 'Dr. Research Director',
            avatar: 'https://ui-avatars.com/api/?name=Research+Director&background=f59e0b&color=fff',
            role: 'Research Director'
          },
          actionTaken: 'downloaded',
          readAt: '2025-01-19T10:15:00Z',
          relatedModule: 'documents'
        },
        {
          id: 'HIST005',
          title: 'Team Meeting Minutes Published',
          message: 'The minutes from yesterday\'s team meeting have been published and are available in the documents section.',
          category: 'events',
          type: 'document_published',
          priority: 'low',
          timestamp: '2025-01-18T15:20:00Z',
          sender: {
            name: 'Meeting Coordinator',
            avatar: 'https://ui-avatars.com/api/?name=Meeting+Coordinator&background=ef4444&color=fff',
            role: 'Administrative'
          },
          actionTaken: 'viewed',
          readAt: '2025-01-18T16:45:00Z',
          relatedModule: 'events'
        },
        // Add more historical notifications for different time periods
        {
          id: 'HIST006',
          title: 'System Maintenance Complete',
          message: 'The scheduled system maintenance has been completed successfully. All services are now fully operational.',
          category: 'support',
          type: 'system_update',
          priority: 'high',
          timestamp: '2025-01-15T06:00:00Z',
          sender: {
            name: 'IT Operations',
            avatar: 'https://ui-avatars.com/api/?name=IT+Operations&background=06b6d4&color=fff',
            role: 'IT'
          },
          actionTaken: 'acknowledged',
          readAt: '2025-01-15T08:30:00Z',
          relatedModule: 'support'
        },
        {
          id: 'HIST007',
          title: 'Holiday Schedule Updated',
          message: 'The holiday schedule for 2025 has been updated. Please review the changes and update your calendar accordingly.',
          category: 'events',
          type: 'schedule_update',
          priority: 'medium',
          timestamp: '2025-01-10T12:00:00Z',
          sender: {
            name: 'HR Manager',
            avatar: 'https://ui-avatars.com/api/?name=HR+Manager&background=f97316&color=fff',
            role: 'Human Resources'
          },
          actionTaken: 'viewed',
          readAt: '2025-01-10T14:20:00Z',
          relatedModule: 'events'
        },
        {
          id: 'HIST008',
          title: 'Forum Discussion: New Medical Guidelines',
          message: 'A new discussion about updated medical guidelines has been started in the Clinical Practice forum.',
          category: 'forums',
          type: 'new_discussion',
          priority: 'medium',
          timestamp: '2025-01-08T10:30:00Z',
          sender: {
            name: 'Dr. Clinical Lead',
            avatar: 'https://ui-avatars.com/api/?name=Clinical+Lead&background=84cc16&color=fff',
            role: 'Clinical Lead'
          },
          actionTaken: 'participated',
          readAt: '2025-01-08T11:00:00Z',
          relatedModule: 'forums'
        },
        {
          id: 'HIST009',
          title: 'Payroll Tax Documents Available',
          message: 'Your 2024 tax documents (W-2 forms) are now available for download in the payroll section.',
          category: 'payroll',
          type: 'document_available',
          priority: 'high',
          timestamp: '2024-12-28T14:00:00Z',
          sender: {
            name: 'Payroll System',
            avatar: 'https://ui-avatars.com/api/?name=Payroll+System&background=f59e0b&color=fff',
            role: 'System'
          },
          actionTaken: 'downloaded',
          readAt: '2024-12-28T15:30:00Z',
          relatedModule: 'payroll'
        },
        {
          id: 'HIST010',
          title: 'Equipment Request Status Update',
          message: 'Your request for new laboratory equipment has been approved and will be processed within the next 2 weeks.',
          category: 'forms',
          type: 'status_update',
          priority: 'medium',
          timestamp: '2024-12-20T13:45:00Z',
          sender: {
            name: 'Procurement Team',
            avatar: 'https://ui-avatars.com/api/?name=Procurement+Team&background=8b5cf6&color=fff',
            role: 'Procurement'
          },
          actionTaken: 'acknowledged',
          readAt: '2024-12-20T14:00:00Z',
          relatedModule: 'forms'
        }
      ];

      setNotifications(mockHistory);
      setLoading(false);
    };

    setTimeout(fetchNotificationHistory, 500);
  }, []);

  const dateRanges = [
    { id: 'all', name: 'All Time' },
    { id: 'today', name: 'Today' },
    { id: 'week', name: 'This Week' },
    { id: 'month', name: 'This Month' },
    { id: 'quarter', name: 'This Quarter' },
    { id: 'year', name: 'This Year' }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', icon: BellIcon },
    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentCheckIcon },
    { id: 'documents', name: 'Documents', icon: DocumentTextIcon },
    { id: 'events', name: 'Events', icon: CalendarIcon },
    { id: 'training', name: 'Training', icon: CheckIcon },
    { id: 'forms', name: 'Forms', icon: DocumentTextIcon },
    { id: 'support', name: 'Support', icon: InformationCircleIcon },
    { id: 'payroll', name: 'Payroll', icon: UserIcon },
    { id: 'forums', name: 'Forums', icon: ChatBubbleLeftIcon }
  ];

  const filterByDateRange = (notifications, range) => {
    if (range === 'all') return notifications;
    
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    return notifications.filter(notification => {
      const notificationDate = new Date(notification.timestamp);
      
      switch (range) {
        case 'today':
          return notificationDate >= startOfToday;
        case 'week':
          return notificationDate >= startOfWeek;
        case 'month':
          return notificationDate >= startOfMonth;
        case 'quarter':
          return notificationDate >= startOfQuarter;
        case 'year':
          return notificationDate >= startOfYear;
        default:
          return true;
      }
    });
  };

  const filteredNotifications = filterByDateRange(
    notifications.filter(notification => {
      const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           notification.sender.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || notification.category === selectedCategory;

      return matchesSearch && matchesCategory;
    }),
    selectedDateRange
  );

  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'sender':
        comparison = a.sender.name.localeCompare(b.sender.name);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      default: // date
        comparison = new Date(a.timestamp) - new Date(b.timestamp);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatReadTime = (readAt) => {
    if (!readAt) return 'Not read';
    const date = new Date(readAt);
    return `Read ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'viewed':
        return 'bg-blue-100 text-blue-800';
      case 'acknowledged':
        return 'bg-green-100 text-green-800';
      case 'downloaded':
        return 'bg-purple-100 text-purple-800';
      case 'participated':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'completion':
        return <CheckIcon className="h-5 w-5" />;
      case 'scheduling':
        return <CalendarIcon className="h-5 w-5" />;
      case 'access_granted':
        return <EyeIcon className="h-5 w-5" />;
      case 'document_published':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'system_update':
        return <ArrowPathIcon className="h-5 w-5" />;
      case 'schedule_update':
        return <CalendarIcon className="h-5 w-5" />;
      case 'new_discussion':
        return <ChatBubbleLeftIcon className="h-5 w-5" />;
      case 'document_available':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'status_update':
        return <InformationCircleIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  // Group notifications by date for timeline view
  const groupNotificationsByDate = (notifications) => {
    const groups = {};
    notifications.forEach(notification => {
      const date = new Date(notification.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });
    return groups;
  };

  const groupedNotifications = groupNotificationsByDate(sortedNotifications);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notification History</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    View and manage your past notifications
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setViewMode(viewMode === 'list' ? 'timeline' : 'list')}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {viewMode === 'list' ? (
                    <>
                      <ChartBarIcon className="h-4 w-4 mr-2" />
                      Timeline View
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                      List View
                    </>
                  )}
                </button>
                <Link
                  to="/notifications"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <BellIcon className="h-4 w-4 mr-2" />
                  Current Notifications
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search notification history..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block w-full sm:w-auto appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="priority-desc">High Priority First</option>
                  <option value="title-asc">Title A-Z</option>
                  <option value="sender-asc">Sender A-Z</option>
                  <option value="category-asc">Category A-Z</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    value={selectedDateRange}
                    onChange={(e) => setSelectedDateRange(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {dateRanges.map(range => (
                      <option key={range.id} value={range.id}>
                        {range.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setSelectedDateRange('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BellIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Notifications</p>
                <p className="text-2xl font-semibold text-gray-900">{notifications.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Viewed</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {notifications.filter(n => n.actionTaken === 'viewed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">High Priority</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {notifications.filter(n => n.priority === 'high').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CalendarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Week</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {filterByDateRange(notifications, 'week').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications Display */}
        {sortedNotifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No notification history found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedCategory !== 'all' || selectedDateRange !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : "You don't have any notification history yet."
              }
            </p>
          </div>
        ) : viewMode === 'timeline' ? (
          // Timeline View
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Timeline View ({sortedNotifications.length} notifications)
              </h3>
            </div>
            <div className="p-6">
              {Object.entries(groupedNotifications).map(([date, dayNotifications]) => (
                <div key={date} className="mb-8 last:mb-0">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 rounded-full p-2 mr-4">
                      <CalendarIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {new Date(date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </h4>
                    <span className="ml-2 text-sm text-gray-500">
                      ({dayNotifications.length} notification{dayNotifications.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="ml-12 space-y-4">
                    {dayNotifications.map((notification) => (
                      <div key={notification.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start">
                            <div className="p-2 bg-gray-100 rounded-lg mr-4">
                              {getTypeIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-sm font-medium text-gray-900 truncate">
                                {notification.title}
                              </h5>
                              <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                                {notification.message}
                              </p>
                              <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                                <div className="flex items-center">
                                  <img
                                    className="h-4 w-4 rounded-full mr-1"
                                    src={notification.sender.avatar}
                                    alt={notification.sender.name}
                                  />
                                  {notification.sender.name}
                                </div>
                                <span>{formatTimestamp(notification.timestamp)}</span>
                                <span className="capitalize">{notification.category}</span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4 flex flex-col items-end space-y-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                              {notification.priority.toUpperCase()}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(notification.actionTaken)}`}>
                              {notification.actionTaken.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // List View
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
              <span className="text-sm font-medium text-gray-700">
                {sortedNotifications.length} notification{sortedNotifications.length !== 1 ? 's' : ''} found
              </span>
            </div>
            <div className="divide-y divide-gray-200">
              {sortedNotifications.map((notification) => (
                <div key={notification.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className="p-2 bg-gray-100 rounded-lg mr-4">
                        {getTypeIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {notification.title}
                          </h3>
                          <div className="flex items-center space-x-2 ml-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                              {notification.priority.toUpperCase()}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(notification.actionTaken)}`}>
                              {notification.actionTaken.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {notification.message}
                        </p>
                        
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500 space-x-4">
                            <div className="flex items-center">
                              <img
                                className="h-4 w-4 rounded-full mr-1"
                                src={notification.sender.avatar}
                                alt={notification.sender.name}
                              />
                              {notification.sender.name}
                            </div>
                            <span>{formatTimestamp(notification.timestamp)}</span>
                            <span className="capitalize">{notification.category}</span>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            {formatReadTime(notification.readAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default NotificationHistory;
